import { convertLexicalJsonToQuery } from '#pplx/frontend/apps/ask/ask-input/query-serialization/lexicalNodeToQuery.js';
import { prependDeepResearchSkill, removeDeepResearchSkill, } from '#pplx/frontend/apps/ask/hooks/ask-deep-research-hooks/deepResearchInput.js';
import { useAsiAccessDecision } from '#pplx/frontend/apps/ask/hooks/computer-access-hooks/useAsiAccessDecision.js';
import { useCurrentResultIds } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { useSearchModeForSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useSearchModeForSearchModel.js';
import { queryInputStateContextProvider, useInputActions, } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { hasDeepResearchPrefill } from '#pplx/frontend/ask-models/deepResearchSkill.js';
import { parseDeepResearchReasoningEffort } from '#pplx/frontend/ask-models/reasoningEffort.js';
import { SearchMode, SearchModel } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { useFeatureFlagsContext } from '#pplx/frontend/experimentation/hooks.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
const modeChangedEvent = EVENT_PAYLOAD;
function isAskDeepResearchComputerSupported() {
    return import.meta.env.VITE_SUPPORTS_ASK_DEEP_RESEARCH_COMPUTER === '1';
}
// Matches the backend's enrollment rule, so an arm it rejects never reaches it.
function isAskDeepResearchTreatment(getFlagValue) {
    const config = getFlagValue({
        flag: 'ask-deep-research-computer-config',
        defaultValue: {},
    });
    return parseDeepResearchReasoningEffort(config) !== null;
}
export function useAskDeepResearchSelection() {
    const { getFlagValue } = useFeatureFlagsContext();
    const isLoggedIn = useIsUserLoggedIn();
    const { isEnterprise } = useIsSubscribed();
    const { currentThreadId, currentStreamId } = useCurrentResultIds();
    const isEligibleContext = isAskDeepResearchComputerSupported() &&
        isLoggedIn &&
        !isEnterprise &&
        !currentThreadId &&
        !currentStreamId;
    const { data: access } = useAsiAccessDecision({
        enabled: isEligibleContext,
    });
    const getMode = useSearchModeForSearchModel();
    const { configuredModel, askDeepResearchModel, setAskDeepResearchModel } = useThreadConfig();
    const inputStore = useContext(queryInputStateContextProvider);
    const { setUserInput, setAskDeepResearchDraftSelection } = useInputActions();
    const { $t } = useIntl();
    const { track } = useInstrumentation();
    const isEligible = isEligibleContext && access?.can_use_computer;
    // Evaluate only during DR interactions, never on homepage render.
    const getComputerModel = useCallback((model) => {
        if (!isEligible || getMode(model) !== SearchMode.RESEARCH)
            return null;
        if (!isAskDeepResearchTreatment(getFlagValue))
            return null;
        return SearchModel.ASI;
    }, [isEligible, getMode, getFlagValue]);
    const apply = useCallback((model, computerModel) => {
        const { userInput: value, userInputJson: json } = inputStore.getState();
        setAskDeepResearchModel(model);
        const next = prependDeepResearchSkill(value, json, $t({
            defaultMessage: 'Deep Research',
            description: 'Branded display name for the Deep Research skill',
            id: 'a1a097df67',
        }));
        setUserInput(next.value, next.json);
        setAskDeepResearchDraftSelection({ researchModel: model, computerModel });
        if (!askDeepResearchModel) {
            track(modeChangedEvent({
                name: 'deep research computer mode changed',
                data: { enabled: true },
            }));
        }
    }, [
        inputStore,
        setAskDeepResearchModel,
        $t,
        setUserInput,
        setAskDeepResearchDraftSelection,
        askDeepResearchModel,
        track,
    ]);
    const clear = useCallback((model) => {
        if (getMode(model) === SearchMode.ASI)
            return;
        const { userInputJson: json } = inputStore.getState();
        // A DR skill picked directly in Computer has no Search-origin marker.
        // Remove it on exit too, before keyword suggestions can re-enter DR.
        if (!askDeepResearchModel &&
            (!isEligible ||
                getMode(configuredModel) !== SearchMode.ASI ||
                !json ||
                !hasDeepResearchPrefill(convertLexicalJsonToQuery(json.root).prefills) ||
                !isAskDeepResearchTreatment(getFlagValue)))
            return;
        if (askDeepResearchModel) {
            setAskDeepResearchDraftSelection(undefined);
            track(modeChangedEvent({
                name: 'deep research computer mode changed',
                data: { enabled: false },
            }));
        }
        if (!json)
            return;
        const next = removeDeepResearchSkill(json);
        setUserInput(next.value, next.json);
    }, [
        inputStore,
        askDeepResearchModel,
        isEligible,
        configuredModel,
        getMode,
        getFlagValue,
        setUserInput,
        setAskDeepResearchDraftSelection,
        track,
    ]);
    return { getComputerModel, apply, clear };
}
//# sourceMappingURL=useAskDeepResearchSelection.js.map